import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Locale, LOCALE_META, localeFromPath, localizePath, stripLocale } from "./config";
import { messages, Messages } from "./messages";

interface I18nState {
  locale: Locale;
  m: Messages;
  /** Localizes an app path ("/materials" → "/zh/materials"). */
  path: (p: string) => string;
  switchTo: (l: Locale) => void;
  formatDate: (iso: string) => string;
}

const I18nContext = createContext<I18nState | null>(null);

export const LOCALE_STORAGE_KEY = "bionexus-locale";

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const locale = localeFromPath(location.pathname);

  useEffect(() => {
    document.documentElement.lang = LOCALE_META[locale].htmlLang;
  }, [locale]);

  const path = useCallback((p: string) => localizePath(p, locale), [locale]);

  const switchTo = useCallback(
    (l: Locale) => {
      try {
        localStorage.setItem(LOCALE_STORAGE_KEY, l);
      } catch {
        // storage unavailable — the URL still carries the choice
      }
      navigate(localizePath(stripLocale(location.pathname), l) + location.search + location.hash);
    },
    [location.hash, location.pathname, location.search, navigate],
  );

  const value = useMemo<I18nState>(
    () => ({
      locale,
      m: messages[locale],
      path,
      switchTo,
      formatDate: (iso: string) =>
        new Date(iso).toLocaleDateString(LOCALE_META[locale].dateLocale, { year: "numeric", month: "long", day: "numeric" }),
    }),
    [locale, path, switchTo],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
};
